import { createCaller } from "@dransay/api";
import { CATEGORIES } from "@dransay/api/models";
import { logger } from "@dransay/logger";
import {
	data,
	type MetaDescriptor,
	redirect,
	type ShouldRevalidateFunction,
	useLoaderData,
	useParams,
	useRouteLoaderData,
} from "react-router";
import { OtcProductDetails } from "~/features/products/otc/otc-product-details.component";
import { ProductDetails } from "~/features/products/product-details.component";
import { useProductViewedEvent } from "~/hooks/use-product-viewed-event.hook";
import { fetchAllPages } from "~/routes/helpers/fetch-all-pages";
import { getProduct } from "~/routes/helpers/get-product";
import { getProductVariants } from "~/routes/helpers/get-product-variants";
import { isOtcPdpVisibleToUser } from "~/routes/helpers/is-otc-pdp-visible";
import {
	getBaseVendorFilter,
	getSelectedVendorId,
	toCarouselFilter,
} from "~/routes/helpers/product-page-vendor-filter";
import { resolveDistanceInput } from "~/routes/helpers/resolve-distance-input";
import { parseProductIdOrThrow404 } from "~/routes/helpers/validate-product-id";
import i18nServer from "~/tooling/i18n/i18n.server";
import { getCurrentMarket } from "~/tooling/market/get-current-market";
import { RouteHistogram } from "~/tooling/metrics.server";
import { getContext } from "~/tooling/trpc/get-context";
import { getImageSource } from "~/utils/default-image";
import { getSearchParams } from "~/utils/get-search-params";
import { getDefaultVendorId } from "~/utils/vendor";
import type { Route } from "./+types/_storefront._products.product.$slug.$productId";
import { getPageNameHandle } from "./helpers/get-page-name-handle";
import { productFormatter } from "./helpers/product-formatter";

const INELIGIBLE_PRODUCT_RATING = {
	eligible: false,
	rating: null,
} as const;

const fetchUserProductRating = async (
	caller: ReturnType<typeof createCaller>,
	isCustomer: boolean,
	productId: number,
) => {
	if (!isCustomer) {
		return INELIGIBLE_PRODUCT_RATING;
	}
	try {
		return await caller.customer.getMyProductRating({ productId });
	} catch (error) {
		logger.error(error, "getMyProductRating failed, hiding rating editor");
		return INELIGIBLE_PRODUCT_RATING;
	}
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
	formAction,
	defaultShouldRevalidate,
}) => {
	// Prevent revalidation for cart-related actions
	if (formAction?.includes("/api/cart")) {
		return false;
	}

	// Prevent revalidation for favorites-related actions
	if (formAction?.includes("/api/favorites")) {
		return false;
	}

	if (formAction?.includes("/api/vendors-filtered")) {
		return false;
	}

	return defaultShouldRevalidate;
};

export async function loader({ params, request }: Route.LoaderArgs) {
	const histogram = new RouteHistogram("product");

	// Validate productId early, before any async operations
	// This prevents invalid requests (like JS file requests matching the route pattern)
	// from triggering unnecessary database queries or tRPC calls
	let parsedProductId: number;
	try {
		parsedProductId = parseProductIdOrThrow404(params.productId);
	} catch (e) {
		histogram.end(404);
		throw e;
	}

	const context = await getContext({ request });
	const caller = createCaller(context);
	const searchParams = getSearchParams({ request });
	const url = new URL(request.url);
	const currentMarket = await getCurrentMarket();
	const debugPostalCode = url.searchParams.get("postalCode") ?? undefined;

	// Profile address is the primary location source (patient requirement);
	// `?postalCode=` is a debug override. Distance is optional for the PDP.
	const distanceInput = await resolveDistanceInput({
		caller,
		role: context.authenticatedUser?.role,
		debugPostalCode,
		defaultCountryCode: currentMarket.phoneDefaultCountryCode,
	});

	const lang = await i18nServer.getLocale(request);

	if (searchParams.search) {
		histogram.end(302);
		throw redirect(`/products?${url.searchParams.toString()}`);
	}

	const filter = getBaseVendorFilter(request);
	const carouselFilter = toCarouselFilter(filter);
	const isCustomer = context.authenticatedUser?.role === "customer";
	const [
		product,
		filteredVendors,
		allVendors,
		{ items: carouselProducts },
		ratingBreakdown,
		userProductRating,
	] = await Promise.all([
		getProduct({ request, productId: String(parsedProductId) }),
		fetchAllPages((page, pageSize) =>
			caller.public.getProductVendors({
				productId: parsedProductId,
				filter,
				page,
				pageSize,
				...distanceInput,
			}),
		),
		fetchAllPages((page, pageSize) =>
			caller.public.getProductVendors({
				productId: parsedProductId,
				filter: { deliveryMethodKind: filter?.deliveryMethodKind },
				page,
				pageSize,
				...distanceInput,
			}),
		),
		caller.public.getCuratedProducts({
			filter: carouselFilter,
			type: "exclusive",
		}),
		caller.public
			.getRatingBreakdown({ productId: parsedProductId })
			.catch((error) => {
				logger.error(error, "getRatingBreakdown failed, falling back to empty");
				return [];
			}),
		fetchUserProductRating(caller, isCustomer, parsedProductId),
	]);

	if (!product) {
		histogram.end(404);
		throw new Response("Not Found", { status: 404 });
	}

	if (
		product.category === CATEGORIES.CANNABIS_ACCESSORIES &&
		!(await isOtcPdpVisibleToUser(
			context.authenticatedUser?.emailAddress,
			context.featureFlagCache,
		))
	) {
		histogram.end(404);
		throw new Response("Not Found", { status: 404 });
	}

	product.imageUrl = getImageSource(product.imageUrl, product.category);

	const selectedVendorId = getSelectedVendorId(request);
	const defaultVendorId = getDefaultVendorId(allVendors, selectedVendorId);

	let variants: Awaited<ReturnType<typeof getProductVariants>> = [];
	if (product.variantGroupId) {
		try {
			variants = await getProductVariants({
				request,
				variantGroupId: product.variantGroupId,
			});
		} catch (error) {
			logger.warn({
				error,
				message: "Failed to fetch product variants in PDP loader",
				productId: parsedProductId,
				variantGroupId: product.variantGroupId,
			});
		}
	}

	histogram.end(200);

	return data({
		product,
		vendors: filteredVendors,
		allVendors,
		defaultVendorId,
		selectedVendorId,
		initialExclusiveVendorId: carouselFilter?.vendorId,
		initialExclusiveDeliveryMethodKind: carouselFilter?.deliveryMethodKind,
		variants,
		carouselProducts: carouselProducts.map((p) => productFormatter(p, lang)),
		ratingBreakdown,
		userProductRating,
	});
}

export function meta({ data }: Route.MetaArgs): MetaDescriptor[] {
	// @todo this isn't the best way to do this
	// https://dransay.atlassian.net/browse/JUP-909
	const {
		ENV: { ENVIRONMENT },
	} = useRouteLoaderData("root") ?? {
		ENV: { ENVIRONMENT: "development" },
	};

	if (!data?.product) {
		return [];
	}

	const productPageTitle = `${data.product.name}: ${data.product.kind} - ${data.product.producer.name}`;

	return [
		{ title: productPageTitle },
		{ name: "description", content: productPageTitle },
		{ property: "og:type", content: "product" },
		{ property: "og:title", content: productPageTitle },
		{ property: "og:description", content: productPageTitle },
		{
			tagName: "meta",
			name: "robots",
			content:
				ENVIRONMENT === "production" ? "index, follow" : "noindex, nofollow",
		},
		{
			tagName: "link",
			rel: "canonical",
			href: `https://shop.dransay.com/product/${data.product.name}/${data.product.id}`,
		},
	];
}

function ProductPage() {
	const { productId } = useParams();

	const loaderData = useLoaderData<Route.ComponentProps["loaderData"]>();
	const { product, vendors } = loaderData;

	useProductViewedEvent({ product, vendors });

	// ensure product details get re-instantiated when the productId changes
	// React 19 auto-preloads <img fetchPriority="high"> from ProductDetailsHeader
	// OTC renders a dedicated PDP that omits the
	// cannabis-only sections; everything else keeps the standard PDP.
	return product.category === CATEGORIES.CANNABIS_ACCESSORIES ? (
		<OtcProductDetails key={productId} />
	) : (
		<ProductDetails key={productId} />
	);
}

export default ProductPage;

export const handle = {
	...getPageNameHandle("productDetails"),
};
